import type { Vehicle } from '@trajectory/astro';
import type { RocketDto } from '@trajectory/contracts';

/**
 * Convert the API's degree/kg-suffixed `RocketDto` into the radian/SI
 * `Vehicle` shape `propagateFlight` expects.
 *
 * This mirrors `toPhysicsVehicle` in `apps/api/src/routes/rockets.ts`
 * exactly. It is duplicated rather than imported because apps/web and
 * apps/api do not depend on each other — the simulation worker needs to run
 * `propagateFlight` locally (so tweaking a rocket re-simulates instantly,
 * with no network round trip), which means this conversion has to exist on
 * both sides of that boundary. If it drifts, `apps/api`'s route tests and a
 * future cross-check test here are the guard-rail.
 */
export function toPhysicsVehicle(dto: RocketDto): Vehicle {
  return {
    id: dto.id,
    name: dto.name,
    payloadMass: dto.payloadMassKg,
    demonstratedReliability: dto.demonstratedReliability,
    fairing: dto.fairing
      ? {
          mass: dto.fairing.massKg,
          diameter: dto.fairing.diameterMeters,
          length: dto.fairing.lengthMeters,
          jettisonAltitude: dto.fairing.jettisonAltitudeMeters,
        }
      : undefined,
    stages: dto.stages.map((stage) => ({
      id: stage.id,
      name: stage.name,
      structuralMass: stage.structuralMassKg,
      tankMass: stage.tankMassKg,
      propellantMass: stage.propellantMassKg,
      tankVolume: stage.tankVolumeCubicMeters,
      engine: {
        id: stage.engine.id,
        name: stage.engine.name,
        thrustVacuum: stage.engine.thrustVacuumNewtons,
        thrustSeaLevel: stage.engine.thrustSeaLevelNewtons,
        ispVacuum: stage.engine.ispVacuumSeconds,
        mass: stage.engine.massKg,
        gimbalRange: (stage.engine.gimbalRangeDeg * Math.PI) / 180,
        minimumThrottle: stage.engine.minimumThrottle,
        maximumThrottle: stage.engine.maximumThrottle,
        propellant: stage.engine.propellant,
        cycle: stage.engine.cycle,
        restartable: stage.engine.restartable,
        unitCost: stage.engine.unitCostUsd,
      },
      engineCount: stage.engineCount,
      diameter: stage.diameterMeters,
      dragCoefficientScale: stage.dragCoefficientScale,
      separationDelay: stage.separationDelaySeconds,
      nominalThrottle: stage.nominalThrottle,
      boosters: stage.boosters
        ? {
            count: stage.boosters.count,
            enginesPerBooster: stage.boosters.enginesPerBooster,
            structuralMass: stage.boosters.structuralMassKg,
            propellantMass: stage.boosters.propellantMassKg,
            referenceArea: stage.boosters.referenceAreaSqm,
            engine: {
              id: stage.boosters.engine.id,
              name: stage.boosters.engine.name,
              thrustVacuum: stage.boosters.engine.thrustVacuumNewtons,
              thrustSeaLevel: stage.boosters.engine.thrustSeaLevelNewtons,
              ispVacuum: stage.boosters.engine.ispVacuumSeconds,
              mass: stage.boosters.engine.massKg,
              gimbalRange: (stage.boosters.engine.gimbalRangeDeg * Math.PI) / 180,
              minimumThrottle: stage.boosters.engine.minimumThrottle,
              maximumThrottle: stage.boosters.engine.maximumThrottle,
              propellant: stage.boosters.engine.propellant,
              cycle: stage.boosters.engine.cycle,
              restartable: stage.boosters.engine.restartable,
              unitCost: stage.boosters.engine.unitCostUsd,
            },
          }
        : undefined,
    })),
  };
}
